import React from 'react';
import { LOCAL_CITY_LOCATION, YOUR_DETECTED_LOCATION, YOUR_SELECTED_LOCATION } from '../../../constants';
import Link from '../../primitives/link';

type LocationInfoHookProps = {
    isMicrosite: boolean;
    isLocalCityPage: boolean;
    isMicrositeHomepage: boolean;
    locationChangedByUser: boolean;
    loading: boolean;
    name?: string;
    geo?: { city?: string; stateShort?: string } | null;
    permalink?: string;
    microsite?: {
        targetCity?: { city?: string | null; state?: string | null } | null;
        franchise?: { address?: { city?: string | null } | null } | null;
    } | null;
    isNationalCallCenter: boolean;
    innerComponentTag: React.ElementType;
};

type LocationInfo = {
    headerText: string | React.ReactNode;
    firstLine: {
        text: string | undefined;
        link: string | undefined;
        Component: React.ElementType;
        InnerComponent: React.ElementType;
    };
    secondLine: {
        hasText: boolean;
        text: string | undefined;
        link: string | undefined;
        Component: React.ElementType;
        InnerComponent: React.ElementType;
    };
};

const useLocationInfo = ({
    isMicrosite,
    isLocalCityPage,
    isMicrositeHomepage,
    locationChangedByUser,
    loading,
    name,
    geo,
    permalink,
    microsite,
    isNationalCallCenter,
    innerComponentTag,
}: LocationInfoHookProps): LocationInfo => React.useMemo(() => {
    const getLocationHeaderText = (): string | React.ReactNode => {
        if (isMicrosite) {
            return (
                <>
                    Here to Help
                    <sup>®</sup>
                    {' '}
                    24/7
                </>
            );
        }
        if (isLocalCityPage) return LOCAL_CITY_LOCATION;
        return locationChangedByUser ? YOUR_SELECTED_LOCATION : YOUR_DETECTED_LOCATION;
    };
    const getFirstLineText = (): string | undefined => {
        if (isMicrosite) return name;
        if (isLocalCityPage) return `${microsite?.targetCity?.city || microsite?.franchise?.address?.city}, ${microsite?.targetCity?.state}`;
        if (!loading) {
            if (locationChangedByUser) return name;
            if (!locationChangedByUser && !!geo && !!geo.city) {
                return `${geo?.city}${geo?.stateShort ? `, ${geo.stateShort}` : ''}`;
            }
        }
        return '';
    };

    const getFirstLineLink = (): string | undefined => {
        if (isMicrosite || isLocalCityPage) return undefined;
        return (name && locationChangedByUser) ? permalink : undefined;
    };

    const hasSecondLine = (): boolean => {
        if (isMicrosite) return false;
        if (isLocalCityPage) return true;
        return !(name && locationChangedByUser);
    };

    const getSecondLineText = (): string | undefined => {
        if (isMicrosite) return '';
        if (isLocalCityPage) return name;
        if (!loading) {
            if (!locationChangedByUser && isNationalCallCenter) {
                return 'For service in your area, please call our National Call Center.';
            }
            if (name && !locationChangedByUser) return name;
        }
        return '';
    };

    const getSecondLineLink = (): string | undefined => {
        if (isMicrosite) return undefined;
        if (isLocalCityPage) return permalink;
        return (name && !locationChangedByUser) ? permalink : undefined;
    };

    return {
        headerText: getLocationHeaderText(),
        firstLine: {
            text: getFirstLineText(),
            link: getFirstLineLink(),
            Component: (() => {
                if (isMicrositeHomepage) return 'h1';
                if (getFirstLineLink()) return Link;
                return 'div';
            })() as React.ElementType,
            InnerComponent: (() => {
                if (isMicrositeHomepage) return innerComponentTag;
                if (getFirstLineLink()) return innerComponentTag;
                return 'p';
            })() as React.ElementType,
        },
        secondLine: {
            hasText: hasSecondLine(),
            text: getSecondLineText(),
            link: getSecondLineLink(),
            Component: (() => {
                if (getSecondLineLink()) return Link;
                return 'div';
            })() as React.ElementType,
            InnerComponent: (() => {
                if (getSecondLineLink()) return innerComponentTag;
                return 'p';
            })() as React.ElementType,
        },
    };
}, [
    isMicrosite,
    isLocalCityPage,
    locationChangedByUser,
    name,
    microsite?.targetCity?.city,
    microsite?.targetCity?.state,
    microsite?.franchise?.address?.city,
    loading,
    geo,
    permalink,
    isNationalCallCenter,
    isMicrositeHomepage,
    innerComponentTag,
]);

export default useLocationInfo;
